import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import './delivery-scheduling.js';
import { initCartDrawerUpsell } from './cart-drawer-upsell-liquid.js';
// --- Cart validation notifications (drawer + cart page) ---
let cartNotificationStylesInjected = false;
function ensureCartNotificationStyles() {
  if (cartNotificationStylesInjected) return;
  cartNotificationStylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    .cart__notification[hidden]{display:none!important;}
    .cart__notification{
      display:flex;align-items:flex-start;justify-content:space-between;gap:12px;
      padding:12px 14px;margin:0 0 12px;
      border-radius:var(--style-border-radius-inputs,10px);
      border:1px solid var(--color-border,#ddd);
      background:rgb(var(--color-foreground-rgb,0 0 0)/0.03);
      color:var(--color-foreground,#111);
      font-size:14px;line-height:1.4;
    }
    .cart__notification--error{
      border-color:var(--color-error,#d32f2f);
      background:rgb(211 47 47/0.08);
      color:var(--color-error,#d32f2f);
    }
    .cart__notification--success{
      border-color:var(--color-success,#2e7d32);
      background:rgb(46 125 50/0.08);
      color:var(--color-success,#2e7d32);
    }
    .cart__notification-text{flex:1;min-width:0;}
    .cart__notification-close{
      flex:0 0 auto;
      width:28px;height:28px;line-height:28px;
      border:0;background:transparent;color:inherit;
      opacity:.75;cursor:pointer;border-radius:999px;
      display:flex;align-items:center;justify-content:center;
    }
    .cart__notification-close:hover{opacity:1;background:rgb(0 0 0/0.06);}
  `;
  document.head.appendChild(style);
}

/**
 * Create/find the notification container inside a scope (drawer dialog).
 * @param {ParentNode} scope
 * @returns {HTMLElement|null}
 */
function getOrCreateCartNotification(scope) {
  ensureCartNotificationStyles();

  const ctas = scope.querySelector?.('.cart__ctas');
  if (!(ctas instanceof HTMLElement)) return null;

  let notification = /** @type {HTMLElement|null} */ (ctas.querySelector('[data-cart-notification]'));
  if (notification) return notification;

  notification = document.createElement('div');
  notification.className = 'cart__notification cart__notification--error';
  notification.setAttribute('data-cart-notification', 'true');
  notification.setAttribute('role', 'alert');
  notification.setAttribute('aria-live', 'assertive');
  notification.hidden = true;
  notification.innerHTML = `
    <div class="cart__notification-text"></div>
    <button type="button" class="cart__notification-close" aria-label="Close">×</button>
  `;

  const closeBtn = notification.querySelector('.cart__notification-close');
  closeBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    notification.hidden = true;
  });

  ctas.insertBefore(notification, ctas.firstChild);
  return notification;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {Map<string, any|null>} */
  #productJsonCache = new Map();
  /** @type {number | null} */
  #notificationTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#resetInitialDialogState();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#bindCheckoutInterception);
    this.addEventListener('click', this.#handleCheckoutClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#bindCheckoutInterception);
    this.removeEventListener('click', this.#handleCheckoutClick, { capture: true });
  }

  /** @param {CustomEvent} event */
  #handleCartAdd = (event) => {
    const source = event?.detail?.data?.source || event?.detail?.sourceId || '';
    const silentSources = new Set([
      'customize-cart-wizard',
      'customize-cart-wizard:delivery-fee-sync',
      'customize-cart-wizard:delivery-fee-clear',
      'customize-cart-wizard:delivery-fee-empty-cart',
      'ccw-delivery-fee-cleanup',
      'delivery-scheduling',
      'delivery-scheduling:save',
      'delivery-scheduling:expire-clear',
      'delivery-scheduling:min-date-clear',
      'cart-drawer-upsell',
    ]);
    const autoOpenSources = new Set([
      'product-form-component',
      'quick-order-quantity',
      'quick-order-remove-all',
      'quick-add',
    ]);

    if (silentSources.has(String(source))) return;
    if (!autoOpenSources.has(String(source))) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #resetInitialDialogState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (dialog.open) {
      try {
        dialog.close();
      } catch (_) {
        // ignore
      }
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Ensure upsell initializes after cold-load + on each open (dialog events don't bubble to document).
    initCartDrawerUpsell();

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Check if cart contains any product with the gift-card tag
   * @returns {Promise<boolean>}
   */
  async #hasGiftCardProduct() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return false;
      
      const cart = await response.json();
      if (!cart || !cart.items || cart.items.length === 0) return false;

      const checks = await Promise.all(
        cart.items.map(async (/** @type {{product_type?: string}} */ item) => {
          return item.product_type == 'gift-card';
        })
      );

      return checks.some(Boolean);
    } catch (error) {
      console.error('Error checking for gift-card products:', error);
      return false;
    }
  }


  /**
   * Open gift details dialog and wait for completion
   * @param {Function|string} proceedCallback - Function to call or URL to navigate to after saving
   */
  async #openGiftDetailsDialog(proceedCallback) {
    const giftDialog = document.getElementById('gift-details-dialog');
    if (!(giftDialog instanceof HTMLDialogElement)) {
      console.warn('Gift details dialog not found');
      return;
    }

    // Wire buttons if not already bound
    if (!giftDialog.dataset.bound) {
      giftDialog.dataset.bound = 'true';

      const cancelBtn = giftDialog.querySelector('[data-gift-cancel]');
      cancelBtn?.addEventListener('click', () => giftDialog.close('cancel'));

      const saveBtn = giftDialog.querySelector('[data-gift-save]');
      saveBtn?.addEventListener('click', async () => {
        try {
          const attrs = this.#collectGiftAttributesFromDialog(giftDialog);
          
          // Validate required fields
          if (!String(attrs.receiver_name || '').trim() || 
              !String(attrs.receiver_phone || '').trim() || 
              !String(attrs.receiver_address || '').trim()) {
            const errorEl = giftDialog.querySelector('.gift-details-dialog__error');
            if (errorEl instanceof HTMLElement) errorEl.hidden = false;
            return;
          }

          // Hide error if validation passes
          const errorEl = giftDialog.querySelector('.gift-details-dialog__error');
          if (errorEl instanceof HTMLElement) errorEl.hidden = true;

          await this.#saveCartAttributes(attrs);
          giftDialog.close('saved');
        } catch (e) {
          console.error('Failed to save gift details:', e);
          const errorEl = giftDialog.querySelector('.gift-details-dialog__error');
          if (errorEl instanceof HTMLElement) {
            const errorText = errorEl.querySelector('.gift-details-dialog__error-text');
            if (errorText) {
              errorText.textContent = 'Could not save gift details. Please try again.';
            }
            errorEl.hidden = false;
          }
        }
      });
    }

    // Hide error initially
    const errorEl = giftDialog.querySelector('.gift-details-dialog__error');
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;

    giftDialog.showModal();

    // Wait for dialog to close
    const result = await new Promise((resolve) => {
      const handler = () => {
        giftDialog.removeEventListener('close', handler);
        resolve(giftDialog.returnValue);
      };
      giftDialog.addEventListener('close', handler);
    });

    if (result === 'saved') {
      if (typeof proceedCallback === 'function') {
        proceedCallback();
      } else {
        window.location.href = proceedCallback || '/checkout';
      }
    }
  }

  /**
   * Collect gift attributes from the gift details dialog
   * @param {HTMLDialogElement} dialog
   */
  #collectGiftAttributesFromDialog(dialog) {
    /** @type {Record<string, string>} */
    const attrs = {};
    
    dialog.querySelectorAll('[data-gift-field]').forEach((/** @type {Element} */ el) => {
      const key = el.getAttribute('data-gift-field');
      if (!key) return;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        attrs[key] = (el.value || '').trim();
      }
    });

    const secret = dialog.querySelector('[data-gift-checkbox="keep_my_identity_secret"]');
    if (secret instanceof HTMLInputElement) {
      attrs.keep_my_identity_secret = secret.checked ? 'Yes' : 'No';
    }
    
    return attrs;
  }

  /** Delivery date TTL in ms (same as delivery-scheduling: 30 minutes). */
  static #DELIVERY_DATE_TTL_MS = 30 * 60 * 1000;

  /**
   * Parse delivery_date_saved_at from number/string into epoch ms.
   * Supports seconds, milliseconds, numeric strings, and ISO date strings.
   * @param {unknown} raw
   * @returns {number}
   */
  static #parseSavedAtMs(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return raw < 1e12 ? raw * 1000 : raw;
    }

    if (typeof raw === 'string') {
      const s = raw.trim();
      if (!s) return 0;
      if (/^\d+(\.\d+)?$/.test(s)) {
        const numeric = Number.parseFloat(s);
        if (Number.isFinite(numeric) && numeric > 0) {
          const normalized = Math.trunc(numeric);
          return normalized < 1e12 ? normalized * 1000 : normalized;
        }
      }
      const parsed = Date.parse(s);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    return 0;
  }

  /**
   * Validate delivery scheduling (required for all orders).
   * Uses same storage (cart attributes) and expiration as delivery-scheduling / customize.
   * @param {Record<string, unknown>} attrs - Cart attributes (delivery_date, delivery_date_saved_at)
   * @param {ParentNode} scope
   * @returns {{valid: boolean, errors: string[]}}
   */
#validateDeliveryScheduling(attrs, scope) {
    const errors = [];
    const schedulingEl = scope.querySelector?.('delivery-scheduling');

    if (!schedulingEl) return { valid: true, errors: [] };

    const savedDate = String(attrs.delivery_date || '').trim();
    const savedAtRaw = attrs.delivery_date_saved_at;
    const savedAt = CartDrawerComponent.#parseSavedAtMs(savedAtRaw);
    const now = Date.now();
    
    // Check if the timestamp in the cart attributes is expired
    const isTimestampExpired = savedAt > 0 && (now - savedAt) > CartDrawerComponent.#DELIVERY_DATE_TTL_MS;
    
    let dateValue = '';
    const dateInput = schedulingEl?.querySelector?.('#delivery-date');

    if (dateInput instanceof HTMLInputElement) {
      const currentInputValue = (dateInput.value || '').trim();
      
      // FIX: If the user has typed something, USE IT, regardless of the old timestamp
      if (currentInputValue) {
        dateValue = currentInputValue;
      } 
      // If input is empty but we have a non-expired saved date, use that
      else if (savedDate && !isTimestampExpired) {
        dateValue = savedDate;
        dateInput.value = savedDate;
      }
    } else {
      dateValue = isTimestampExpired ? '' : savedDate;
    }

    if (!dateValue) {
      errors.push('Delivery date is required');
    }

    const schedulingWithValidation = /** @type {any} */ (schedulingEl);
    if (errors.length === 0 && dateInput instanceof HTMLInputElement && typeof schedulingWithValidation?.validateDateInput === 'function') {
      const validation = schedulingWithValidation.validateDateInput({ report: false });
      if (!validation.valid) {
        errors.push(validation.message || 'Selected delivery date is unavailable');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * @param {ParentNode} scope
   * @param {string} message
   * @param {'error'|'success'} type
   */
  #showNotification(scope, message, type = 'error') {
    const notification = getOrCreateCartNotification(scope);
    if (!notification) return;

    const textEl = notification.querySelector('.cart__notification-text');
    if (textEl) textEl.textContent = message;

    notification.className = `cart__notification cart__notification--${type}`;
    notification.hidden = false;
    notification.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    if (this.#notificationTimer) clearTimeout(this.#notificationTimer);
    this.#notificationTimer = window.setTimeout(() => {
      notification.hidden = true;
      this.#notificationTimer = null;
    }, 8000);
  }

  #bindCheckoutInterception = () => {
    // Called on DialogOpenEvent; ensures sticky state is updated.
    // Actual checkout validation runs via #handleCheckoutClick (bound in connectedCallback).
  };

  #handleCheckoutClick = async (/** @type {MouseEvent} */ e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || !dialog.open) return;

    const checkoutControl = target.closest(
      'a.cart__checkout-button, a[href^="/checkout"], a[href*="/checkout"], #checkout, [name="checkout"], button[type="submit"][form="cart-form"], input[type="submit"][form="cart-form"]'
    );
    if (!(checkoutControl instanceof HTMLElement)) return;
    if (checkoutControl.closest('.cart__continue-shopping')) return;

    const checkoutLink = checkoutControl instanceof HTMLAnchorElement ? checkoutControl : null;
    const checkoutButton = checkoutControl;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    try {
      const resume = () => this.#proceedToCheckout(checkoutLink, checkoutButton);

      const deliverySection = dialog.querySelector('delivery-scheduling');
      const deliverySchedulingApi = /** @type {any} */ (deliverySection);
      if (deliverySchedulingApi && typeof deliverySchedulingApi.saveDeliveryData === 'function') {
        try {
          await deliverySchedulingApi.saveDeliveryData();
        } catch (_) {
          // ignore
        }
      }

      const cart = await (
        await fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        })
      ).json();
      const attrs = cart.attributes || {};

      

      const deliveryValidation = this.#validateDeliveryScheduling(attrs, dialog);

      if (!deliveryValidation.valid) {
        const errorMsg = deliveryValidation.errors.length > 0
          ? deliveryValidation.errors.join('. ') + '. Please select delivery scheduling details before checkout.'
          : 'Please select delivery scheduling details before checkout.';
        this.#showNotification(dialog, errorMsg, 'error');

        const deliverySection = dialog.querySelector('delivery-scheduling');
        if (deliverySection) {
          const details = deliverySection.querySelector('details');
          if (details && !details.open) {
            details.open = true;
          }
          deliverySection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        return;
      }

      resume();
    } catch (err) {
      console.error('Failed to process checkout:', err);
      this.#showNotification(dialog, 'Could not validate checkout. Please try again.', 'error');
    }
  };

  /**
   * Proceed with the platform's normal checkout behavior.
   * Uses requestSubmit(submitter) so the submitter name="checkout" is included when tied to a form.
   * @param {Element|null} checkoutLink
   * @param {Element} checkoutButton
   */
  #proceedToCheckout(checkoutLink, checkoutButton) {
    this.closeDialog();
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (checkoutLink && checkoutLink instanceof HTMLAnchorElement && checkoutLink.href) {
      window.location.href = checkoutLink.href;
      return;
    }

    if (checkoutButton instanceof HTMLButtonElement || checkoutButton instanceof HTMLInputElement) {
      const form = checkoutButton.form || null;
      if (form instanceof HTMLFormElement) {
        if (typeof form.requestSubmit === 'function') {
          try {
            form.requestSubmit(checkoutButton);
            return;
          } catch (e) {
            // fall through
          }
        }

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = checkoutButton.getAttribute('name') || 'checkout';
        hidden.value = '1';
        form.appendChild(hidden);
        form.submit();
        hidden.remove();
        return;
      }
    }

    window.location.href = '/checkout';
  }


/**
 * @param {Record<string, string>} attributes
 * @returns {Promise<any>}
 */
async #saveCartAttributes(attributes) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ attributes }),
    credentials: 'same-origin',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`cart/update.js failed: ${res.status} ${text}`);
  }

  // Returns updated cart JSON
  return res.json();
}

}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
